import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Building2, Trash2, Settings, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

const AdminAllProperties = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [fixingRefs, setFixingRefs] = useState(false);
  const [fixingAll, setFixingAll] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignDialog, setAssignDialog] = useState({ open: false, property: null });
  const [assignData, setAssignData] = useState({ owner_id: "", agent_id: "" });

  const canManageUsers = user?.role === "admin" || user?.role === "people_culture";

  useEffect(() => {
    fetchProperties();
    // Only fetch users if user has permission (admin/people_culture)
    if (canManageUsers) {
      fetchUsers();
    }
  }, [canManageUsers]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/admin/properties`, { withCredentials: true });
      setProperties(response.data.properties);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch properties");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const fixPropertyRefs = async () => {
    setFixingRefs(true);
    try {
      const response = await axios.post(`${API}/admin/fix-property-refs`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchProperties();
    } catch (error) {
      toast.error("Failed to fix property references");
    } finally {
      setFixingRefs(false);
    }
  };

  const fixAllData = async () => {
    setFixingAll(true);
    try {
      const response = await axios.post(`${API}/admin/fix-all-data`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchProperties();
    } catch (error) {
      toast.error("Failed to fix data");
    } finally {
      setFixingAll(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!confirm("Delete this property?")) return;
    try {
      await axios.delete(`${API}/admin/properties/${propertyId}`, { withCredentials: true });
      toast.success("Property deleted");
      fetchProperties();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const openAssignDialog = (property) => {
    setAssignData({
      owner_id: property.owner_id || "",
      agent_id: property.agent_id || ""
    });
    setAssignDialog({ open: true, property });
  };

  const handleAssign = async () => {
    try {
      const dataToSend = {};
      if (assignData.owner_id) dataToSend.owner_id = assignData.owner_id;
      if (assignData.agent_id) dataToSend.agent_id = assignData.agent_id;
      
      await axios.put(
        `${API}/admin/properties/${assignDialog.property.property_id}/assign`,
        dataToSend,
        { withCredentials: true }
      );
      toast.success("Property assignment updated");
      setAssignDialog({ open: false, property: null });
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign property");
    }
  };

  const formatPrice = (price, listingType) => {
    return `K ${price?.toLocaleString()}${listingType === "rent" ? "/mo" : ""}`;
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-serif">All Properties</h2>
          <p className="text-muted-foreground text-sm">
            {user?.role === "data_entry" 
              ? `Properties in your branch. Total: ${total}`
              : `View and manage all properties from all agents. Total: ${total}`
            }
          </p>
        </div>
        {user?.role === "admin" && (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={fixAllData} disabled={fixingAll} variant="default" className="bg-primary">
              {fixingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
              Fix All Data
            </Button>
            <Button onClick={fixPropertyRefs} disabled={fixingRefs} variant="outline">
              {fixingRefs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
              Fix References
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="divide-y divide-border">
          {properties.map((property) => (
            <div key={property.property_id} className="p-4 flex items-center gap-4 hover:bg-muted/50 flex-wrap">
              <img 
                src={property.images?.[0] || "https://placehold.co/100x100?text=No+Image"} 
                alt={property.title} 
                className="w-16 h-16 object-cover rounded" 
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="font-medium truncate">{property.title}</p>
                  {property.ref_number && (
                    <span className="text-xs font-mono bg-primary/10 px-2 py-0.5 rounded">{property.ref_number}</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{property.city} • {property.property_type}</p>
                <p className="text-sm font-medium text-primary">{formatPrice(property.price, property.listing_type)}</p>
              </div>
              <div className="text-right text-xs space-y-1">
                <div>
                  <span className="text-muted-foreground">Owner: </span>
                  <span className="font-medium">{property.owner?.name || property.owner?.email || "None"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Agent: </span>
                  <span className="font-medium">{property.agent?.name || property.agent?.email || "None"}</span>
                </div>
              </div>
              <Badge variant={property.status === "active" ? "default" : property.status === "rented" ? "secondary" : "destructive"}>
                {property.status}
              </Badge>
              {user?.role === "admin" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openAssignDialog(property)}>
                    <UserCog className="w-4 h-4 mr-1" /> Assign
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(property.property_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Assign Property Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => setAssignDialog({ open, property: assignDialog.property })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Property</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Assign <strong>{assignDialog.property?.title}</strong> ({assignDialog.property?.ref_number}) to a different owner or agent.
            </p>
            
            <div>
              <label className="block text-sm font-medium mb-2">Owner</label>
              <Select value={assignData.owner_id} onValueChange={(v) => setAssignData({...assignData, owner_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email}) - {u.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Agent</label>
              <Select value={assignData.agent_id} onValueChange={(v) => setAssignData({...assignData, agent_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select agent" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.role === "agent" || u.role === "admin").map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAssignDialog({ open: false, property: null })}>
                Cancel
              </Button>
              <Button onClick={handleAssign}>
                Save Assignment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAllProperties;
